import type { DaySchedule } from "@/lib/api/types";

const DAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

export type Day = (typeof DAYS)[number];

export type TimeSlot = {
  open: string; // "HH:mm"
  close: string; // "HH:mm"
};

export type DayState = {
  enabled: boolean;
  slots: TimeSlot[];
};

export type ScheduleFormState = {
  monday: DayState;
  tuesday: DayState;
  wednesday: DayState;
  thursday: DayState;
  friday: DayState;
  saturday: DayState;
  sunday: DayState;
};

export { DAYS };

// ─── Helpers ──────────────────────────────────────────────────────────────────

function timeToMin(t: string): number {
  const [h, m] = t.split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

export const DEFAULT_SLOT: TimeSlot = { open: "09:00", close: "22:00" };

// ─── Conversions ──────────────────────────────────────────────────────────────

export function scheduleToForm(schedule?: Record<string, DaySchedule[]>): ScheduleFormState {
  const init = (day: Day): DayState => {
    const daySlots = schedule?.[day];
    if (daySlots && daySlots.length > 0) {
      return {
        enabled: true,
        slots: daySlots.map((s) => ({ open: s.open, close: s.close })),
      };
    }
    return { enabled: false, slots: [] };
  };
  return {
    monday: init("monday"),
    tuesday: init("tuesday"),
    wednesday: init("wednesday"),
    thursday: init("thursday"),
    friday: init("friday"),
    saturday: init("saturday"),
    sunday: init("sunday"),
  };
}

export function formToSchedule(form: ScheduleFormState): Record<string, DaySchedule[]> {
  const result: Record<string, DaySchedule[]> = {};
  for (const day of DAYS) {
    const d = form[day];
    if (d.enabled && d.slots.length > 0) {
      result[day] = d.slots.map((s) => ({ open: s.open, close: s.close }));
    }
  }
  return result;
}

// ─── Validation ───────────────────────────────────────────────────────────────

/**
 * Validates store opening hours. Returns the first error found as a string, or
 * null if valid. Used by StoreHoursDialog (toast-based error display).
 */
export function validateSchedule(form: ScheduleFormState): string | null {
  const errors = validateRawSchedule(formToSchedule(form));
  if (!errors) return null;
  for (const day of DAYS) {
    if (errors[day]) return errors[day]!;
  }
  return null;
}

/**
 * Same overnight-aware validation as validateSchedule but accepts a raw
 * Record<day, slots[]> instead of ScheduleFormState. Returns a map of
 * day → error message for every invalid day, or null if all days are valid.
 */
export function validateRawSchedule(
  schedule: Record<string, DaySchedule[]>
): Record<string, string> | null {
  const cap = (d: string) => `${d[0]!.toUpperCase()}${d.slice(1)}`;
  const dayIntervals: Record<string, { openMin: number; closeMin: number }[]> = {};
  const errors: Record<string, string> = {};

  for (const day of DAYS) {
    const slots = schedule[day];
    if (!slots || slots.length === 0) continue;

    const intervals: { openMin: number; closeMin: number }[] = [];
    let dayError = false;
    for (const slot of slots) {
      if (!slot.open || !slot.close) continue;
      const openMin = timeToMin(slot.open);
      const closeMin = timeToMin(slot.close);
      if (openMin === closeMin) {
        errors[day] = `${cap(day)}: open and close times cannot be the same`;
        dayError = true;
        break;
      }
      const closeNorm = closeMin < openMin ? closeMin + 1440 : closeMin;
      intervals.push({ openMin, closeMin: closeNorm });
    }

    if (dayError) continue;

    intervals.sort((a, b) => a.openMin - b.openMin);

    for (let i = 1; i < intervals.length; i++) {
      if (intervals[i - 1]!.closeMin > intervals[i]!.openMin) {
        errors[day] = `${cap(day)}: time slots overlap`;
        dayError = true;
        break;
      }
    }

    if (dayError) continue;

    const last = intervals[intervals.length - 1]!;
    if (last.closeMin > 1440 && last.closeMin % 1440 > intervals[0]!.openMin) {
      errors[day] = `${cap(day)}: overnight slot overlaps with an earlier slot`;
      dayError = true;
    }

    if (!dayError) dayIntervals[day] = intervals;
  }

  for (let i = 0; i < DAYS.length; i++) {
    const day = DAYS[i]!;
    const intervals = dayIntervals[day];
    if (!intervals) continue;
    const last = intervals[intervals.length - 1]!;
    if (last.closeMin <= 1440) continue;
    const nextDay = DAYS[(i + 1) % DAYS.length]!;
    const nextIntervals = dayIntervals[nextDay];
    if (!nextIntervals) continue;
    if (last.closeMin % 1440 > nextIntervals[0]!.openMin)
      errors[day] = `${cap(day)} overnight slot overlaps with ${cap(nextDay)}'s first slot`;
  }

  return Object.keys(errors).length > 0 ? errors : null;
}

// ─── Display ──────────────────────────────────────────────────────────────────

/** Compact weekly-schedule summary for list rows, e.g. "5 days/week" — full hours are one tap away in the edit dialog. */
export function summarizeScheduleDayCount(schedule: Record<string, DaySchedule[]>): string {
  const count = DAYS.filter((d) => (schedule[d] ?? []).length > 0).length;
  return `${count} day${count === 1 ? "" : "s"}/week`;
}

/** Formats a "HH:mm" time string as 12-hour clock time, e.g. "09:00" -> "9:00 AM". */
export function formatScheduleTime(t: string): string {
  const [hStr, mStr] = t.split(":");
  const h = parseInt(hStr ?? "0", 10);
  const m = (mStr ?? "00").padStart(2, "0");
  const period = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${m} ${period}`;
}
